import React, { useState, useEffect } from 'react';
import { Brain, BookOpen, Sparkles, HeartHandshake, Users, Coins, Heart, MessageCircle, Send, Loader2, Trash2 } from 'lucide-react';

export default function App() {
  const [inputText, setInputText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('familia');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const savedHistory = localStorage.getItem('ruach_history');
    if (savedHistory) setHistory(JSON.parse(savedHistory));
  }, []);

  useEffect(() => {
    localStorage.setItem('ruach_history', JSON.stringify(history));
  }, [history]);

  const categories = [
    { id: 'familia', name: 'Familia', icon: Users },
    { id: 'amor', name: 'Amor y Pareja', icon: Heart },
    { id: 'finanzas', name: 'Finanzas', icon: Coins },
    { id: 'social', name: 'Social', icon: MessageCircle },
    { id: 'personal', name: 'Crecimiento', icon: HeartHandshake },
  ];

  const clearHistory = () => {
    if (window.confirm("¿Deseas borrar tu historial confidencial?")) {
      setHistory([]);
      localStorage.removeItem('ruach_history');
    }
  };

  const simulateAIOrchestration = async () => {
    if (!inputText.trim()) return;
    
    setIsProcessing(true);
    const userMessage = inputText;
    setInputText('');

    setProcessingStep('Conectando con Ruach AI...');

    try {
      const response = await fetch('https://ruach-api-723499001696.us-central1.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inputText: userMessage,
          category: selectedCategory
        })
      });

      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status}`);
      }

      const data = await response.json();

      const newResponse = {
        id: Date.now(),
        prompt: userMessage,
        category: selectedCategory,
        psychology: data.psychology || "No se pudo generar respuesta clínica.",
        theology: data.theology || "No se pudo consultar sabiduría bíblica.",
        synthesis: data.synthesis || "Fallo en la síntesis."
      };

      setHistory(prev => [newResponse, ...prev]);
    } catch (error) {
      alert("Error conectando al servidor: " + error.message);
    }

    setIsProcessing(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans flex flex-col">
      <header className="bg-indigo-900 text-white p-6 shadow-md">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-amber-400" />
            <div>
              <h1 className="text-2xl font-bold">Ruach AI</h1>
              <p className="text-indigo-200 text-sm">Psicología & Sabiduría Bíblica</p>
            </div>
          </div>
          {history.length > 0 && (
            <button onClick={clearHistory} className="p-2 hover:bg-indigo-800 rounded-full transition-colors" title="Borrar historial">
              <Trash2 className="w-5 h-5 text-indigo-300" />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 mt-6 grid md:grid-cols-2 gap-8 flex-grow w-full">
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold mb-4 text-slate-700">1. ¿Qué área te preocupa?</h2>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === cat.id ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {cat.name}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold mb-4 text-slate-700">2. Desahógate</h2>
            <textarea
              className="w-full h-40 p-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
              placeholder="Escribe aquí..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <button
              onClick={simulateAIOrchestration}
              disabled={isProcessing || !inputText.trim()}
              className="w-full mt-4 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-50"
            >
              {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              {isProcessing ? 'Buscando Consejo...' : 'Recibir Consejo Integral'}
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 min-h-[500px] overflow-y-auto max-h-[600px]">
          <h2 className="text-lg font-semibold mb-4 text-slate-700 border-b pb-2">Respuestas Orquestadas</h2>
          
          {isProcessing && (
            <div className="flex flex-col items-center py-10 text-indigo-600">
              <Loader2 className="w-10 h-10 animate-spin mb-4" />
              <p className="font-medium animate-pulse">{processingStep}</p>
            </div>
          )}

          <div className="space-y-8">
            {history.map((item) => (
              <div key={item.id} className="border-b border-slate-100 pb-6 animate-in fade-in slide-in-from-bottom-2">
                <p className="text-xs font-bold uppercase text-indigo-400 mb-2">{item.category}</p>
                <p className="text-slate-500 italic mb-4 text-sm">" {item.prompt} "</p>
                
                <div className="space-y-4">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-1 text-blue-800 text-sm font-bold"><Brain className="w-4 h-4"/> Psicología</div>
                    <p className="text-slate-700 text-sm">{item.psychology}</p>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-2 mb-1 text-amber-800 text-sm font-bold"><BookOpen className="w-4 h-4"/> Torá</div>
                    <p className="text-slate-700 text-sm">{item.theology}</p>
                  </div>
                  <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                    <div className="flex items-center gap-2 mb-1 text-indigo-800 text-sm font-bold"><Sparkles className="w-4 h-4"/> Síntesis Práctica</div>
                    <div className="text-slate-700 text-sm space-y-1">
                       {item.synthesis.split('\n').map((line, i) => <p key={i}>{line}</p>)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {!isProcessing && history.length === 0 && (
              <div className="text-center text-slate-400 py-20">
                <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>Tu historial aparecerá aquí de forma privada.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="max-w-4xl mx-auto p-6 text-center w-full">
        <div className="bg-slate-100 p-4 rounded-xl border border-slate-200 mb-6">
          <p className="text-xs text-slate-600">
            🔒 <strong>Confidencialidad:</strong> No guardamos tus datos. El historial reside solo en este navegador.
          </p>
        </div>
        <a
          href="https://link.mercadopago.com.co/appdonacion"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-8 py-3 rounded-full font-bold transition-all shadow-lg hover:scale-105"
        >
          <Heart className="w-5 h-5 fill-current" />
          Aporte Voluntario
        </a>
        <p className="text-[10px] text-slate-400 mt-4">Ruach AI v1.0 - Sabiduría para el Alma</p>
      </footer>
    </div>
  );
}